import classNames from 'classnames';
import React, { useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUpdatePlaceBalance } from '../../services/queries';
import { convertToSmallestUnit, normalizeAmount, validateAmount } from '../../utils/validation';

import styles from './BalanceManager.module.scss';

interface BalanceManagerProps {
  deviceId: number;
  placeId: number;
  currency: string;
}

const BalanceManager: React.FC<BalanceManagerProps> = ({ deviceId, placeId, currency }) => {
  const [amount, setAmount] = useState<string>('');

  const updateBalanceMutation = useUpdatePlaceBalance();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;

    if (value === '' || value === '.') {
      setAmount(value);
      return;
    }

    const parts = value.split('.');
    if (parts.length === 2 && parts[1].length > 2) {
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setAmount(value);
  };

  const handleOperation = async (operation: 'deposit' | 'withdraw'): Promise<void> => {
    if (amount === '' || amount === '.') {
      toast.error('Введите сумму');
      return;
    }

    if (!validateAmount(amount)) {
      toast.error(
        'Некорректная сумма. Используйте формат: 123.45 (максимум 2 знака после запятой)'
      );
      return;
    }

    const normalizedAmount = normalizeAmount(amount);

    if (normalizedAmount <= 0) {
      toast.error('Сумма должна быть больше нуля');
      return;
    }

    // Конвертируем в минимальные единицы валюты (копейки/центы) для API
    const amountInSmallestUnit = convertToSmallestUnit(normalizedAmount);
    const delta = operation === 'deposit' ? amountInSmallestUnit : -amountInSmallestUnit;

    try {
      await updateBalanceMutation.mutateAsync({
        deviceId,
        placeId,
        delta,
      });

      setAmount('');

      const operationText = operation === 'deposit' ? 'Внесено' : 'Снято';
      toast.success(`${operationText} ${normalizedAmount.toFixed(2)} ${currency}`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Произошла ошибка при выполнении операции';
      toast.error(errorMessage);
    }
  };

  const isLoading = updateBalanceMutation.isPending;

  return (
    <div className={styles.balanceOperation}>
      <Form.Group className={classNames('mb-3')}>
        <Form.Label>Сумма операции</Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            className={styles.amountInput}
            disabled={isLoading}
          />
          <InputGroup.Text>{currency}</InputGroup.Text>
        </InputGroup>
      </Form.Group>

      <div className={classNames('d-grid', 'gap-2')}>
        <Button
          variant="success"
          onClick={() => handleOperation('deposit')}
          disabled={isLoading}
          size="lg"
        >
          {isLoading ? 'Обработка...' : 'Deposit (Внести)'}
        </Button>
        <Button
          variant="danger"
          onClick={() => handleOperation('withdraw')}
          disabled={isLoading}
          size="lg"
        >
          {isLoading ? 'Обработка...' : 'Withdraw (Снять)'}
        </Button>
      </div>
    </div>
  );
};

export default BalanceManager;
